import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Send, EyeOff, Unlink, Tv } from 'lucide-react';
import { api } from '../api';

export default function DeviceManager({ socket, userId }) {
  const [devices, setDevices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({}); // deviceUUID -> templateId
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, t] = [await api.listDevices(userId), await api.listTemplates(userId)];
      setDevices(d.devices);
      setTemplates(t.templates);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [userId]);

  useEffect(() => {
    load();
    const i = setInterval(load, 8000); // refresh online status periodically
    return () => clearInterval(i);
  }, [load]);

  const push = (deviceUUID) => {
    const templateId = selected[deviceUUID] || (templates[0] && templates[0].id);
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl || !socket) return;
    socket.emit('display_command', {
      command: 'show_template',
      params: { id: tpl.id, name: tpl.name, ...tpl.json },
      userId,
      deviceId: deviceUUID,
    });
    setTimeout(load, 1000);
  };

  const clear = (deviceUUID) => {
    socket && socket.emit('display_command', { command: 'clear_template', params: {}, userId, deviceId: deviceUUID });
    setTimeout(load, 1000);
  };

  const unclaim = async (deviceUUID) => {
    if (!window.confirm('Remove this TV from your account?')) return;
    await api.unclaimDevice(deviceUUID, userId);
    load();
  };

  const templateName = (id) => templates.find((t) => t.id === id)?.name || '—';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ color: '#9aa7bd' }}>{devices.length} device(s)</span>
        <button className="button" style={{ background: '#374151' }} onClick={load}>
          <RefreshCw size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Refresh
        </button>
      </div>
      {error && <p style={{ color: '#f87171' }}>{error}</p>}
      {devices.length === 0 && <p style={{ color: '#9aa7bd' }}>No paired TVs yet. Pair one by scanning the QR code shown on the screen.</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {devices.map((d) => (
          <div key={d.deviceUUID} style={{ background: '#111827', borderRadius: 10, padding: 16, border: '1px solid #1f2a3d', color: '#e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Tv size={18} />
              <strong>{d.name || d.deviceUUID.slice(0, 8)}</strong>
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.connected ? '#22c55e' : '#64748b' }} />
                {d.connected ? 'Online' : 'Offline'}
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#9aa7bd', marginBottom: 12 }}>
              Showing: {templateName(d.assignedTemplateId)}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select value={selected[d.deviceUUID] || ''} onChange={(e) => setSelected((s) => ({ ...s, [d.deviceUUID]: e.target.value }))}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #334155', background: '#0b1020', color: '#e5e7eb' }}>
                <option value="">Select template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button className="button" onClick={() => push(d.deviceUUID)} title="Push to TV">
                <Send size={16} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="button" style={{ background: '#374151', flex: 1 }} onClick={() => clear(d.deviceUUID)}>
                <EyeOff size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Clear
              </button>
              <button className="button" style={{ background: '#7f1d1d', flex: 1 }} onClick={() => unclaim(d.deviceUUID)}>
                <Unlink size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Unpair
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
